/*
 * The consumer-app registration queue (audit Phase 3.3). Replaces
 * wwwroot/js/RegistrationRequest/List.js, Detail.js and _namespace.js on the
 * Phase 3.1 core. No jQuery.
 *
 * One surface: the Registrations tab of an Application, and a review dialog
 * over it. Approve and Deny are both irreversible and both send mail, so each
 * confirms first and says what the mail will be.
 *
 * The dialog carries the Application it was opened from, stamped on the modal
 * element rather than held in a module variable: the legacy code kept it in
 * `RegistrationRequest._currentApplicationId`, which survived a navigation
 * away from the Application and would then refresh a tab belonging to a
 * different one.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { escapeHtml, one } from '../core/dom';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';

const MODAL = 'RegistrationRequestModal';
const TAB = 'ApplicationRegistrationsTab';

/** The refusal message the Web action returns beside `success: false`. */
function messageOf(data: unknown, fallback: string): string {
    const message = (data as { message?: unknown } | null)?.message;
    return typeof message === 'string' && message ? message : fallback;
}

/** The Application whose queue the open dialog belongs to, or 0. */
function applicationId(): number {
    const parsed = Number.parseInt(one(`#${MODAL}`)?.getAttribute('data-application-id') ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Redraw the queue behind the dialog. The modal element is INSIDE the list
 * partial, so the swap has to wait for the dialog to finish hiding -- replacing
 * the container mid-transition detaches the dialog and `hidden.bs.modal` then
 * fires on a node nothing can hear, leaving the page with a stuck backdrop.
 */
function closeAndRefresh(): void {
    const target = applicationId();
    hideModalThen(MODAL, () => { if (target) void List.Refresh(target); });
}

export const List = {
    Refresh(appId: number | string): Promise<void> {
        return ajaxSwap({
            container: TAB,
            url: `RegistrationRequest/Application/${appId}`,
            alertTitle: 'Failed to load pending registrations',
        });
    },

    OpenDetail(requestId: string, appId: number | string): void {
        // The list partial stamps this too; re-stamping here keeps the two in
        // step if a row is ever rendered outside its own queue.
        one(`#${MODAL}`)?.setAttribute('data-application-id', String(appId));
        void ajaxModal({
            modal: MODAL,
            url: `RegistrationRequest/Detail/${requestId}`,
            errorTitle: 'Registration Request',
            errorMessage: 'Failed to load registration request detail',
        });
    },
};

export const Detail = {
    /**
     * Approve. The Api creates the Person and mails a setup link, so the
     * confirmation names the Domain the request is about to be approved INTO
     * -- the select defaults to "(use app default)" and the operator cannot
     * otherwise see which Domain that is going to be.
     */
    Approve(requestId: string): void {
        const select = one('#ApproveDomainId') as HTMLSelectElement | null;
        const domainId = select?.value ?? '';
        const domainName = domainId
            ? select?.options[select.selectedIndex]?.text ?? domainId
            : null;
        void confirm({
            title: 'Approve registration',
            html: 'Approve this registration?<br/><br/>'
                + 'A Person is created and a setup link is emailed immediately. '
                + (domainName
                    ? `They are placed in <strong>${escapeHtml(domainName)}</strong>.`
                    : 'They are placed in the Application\'s default Domain.'),
            confirmText: 'Approve',
            variant: 'primary',
            onConfirm: () => {
                void ajaxJson({
                    // A plain object, form-encoded: the action binds [FromForm].
                    // An empty domainId is omitted rather than sent blank, so the
                    // nullable int binds to null instead of failing to bind.
                    url: `RegistrationRequest/${requestId}/Approve`,
                    method: 'POST',
                    data: domainId ? { domainId } : {},
                    onSuccess: () => {
                        toast('success', 'Registration approved', 'Setup link emailed.');
                        closeAndRefresh();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to approve', messageOf(data, 'The request was refused.'));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to approve', error);
                        return true;
                    },
                });
            },
        });
    },

    /**
     * Deny. The reason is optional but it is quoted verbatim in the denial
     * email, so the confirmation says whether one is going out.
     */
    Deny(requestId: string): void {
        const reason = ((one('#DenyReason') as HTMLTextAreaElement | null)?.value ?? '').trim();
        void confirm({
            title: 'Deny registration',
            html: 'Deny this registration?<br/><br/>'
                + 'A denial email is sent immediately and the request cannot be reopened. '
                + (reason
                    ? 'Your reason is quoted in it.'
                    : '<span class="text-warning">No reason was given, so the email will not say why.</span>'),
            confirmText: 'Deny',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `RegistrationRequest/${requestId}/Deny`,
                    method: 'POST',
                    data: { reason },
                    onSuccess: () => {
                        toast('warning', 'Registration denied');
                        closeAndRefresh();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to deny', messageOf(data, 'The request was refused.'));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to deny', error);
                        return true;
                    },
                });
            },
        });
    },
};
